// Barra de filtros: búsqueda, caras de los dueños como filtro rápido, "Agregar filtro" y chips de filtros activos.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CasoResumen, Prioridad, TipoCaso } from '../api/tipos';
import { ETIQUETA_PRIORIDAD, ETIQUETA_TIPO, PRIORIDADES } from '../lib/etiquetas';
import { FILTROS_VACIOS, hayFiltros, SIN_DUENO, type FiltrosRapidos } from '../lib/filtros';
import { IconoPrioridad, IconoTipo } from './Chips';
import { Icono } from './Iconos';
import { Avatar, ItemMenu, Popover, SeparadorMenu } from './Ui';

const TIPOS = Object.keys(ETIQUETA_TIPO) as TipoCaso[];

export function BarraFiltros({
  casos,
  filtros,
  alCambiar,
  extra,
  nombres,
}: {
  casos: CasoResumen[];
  filtros: FiltrosRapidos;
  alCambiar: (f: FiltrosRapidos) => void;
  extra?: ReactNode;
  nombres: Map<string, string>;
}) {
  const [texto, setTexto] = useState(filtros.q);
  useEffect(() => setTexto(filtros.q), [filtros.q]);
  useEffect(() => {
    if (texto === filtros.q) return;
    const t = setTimeout(() => alCambiar({ ...filtros, q: texto }), 250);
    return () => clearTimeout(t);
  }, [texto, filtros, alCambiar]);

  const duenos = useMemo(() => {
    const cuenta = new Map<string, number>();
    for (const c of casos) cuenta.set(c.dueno ?? SIN_DUENO, (cuenta.get(c.dueno ?? SIN_DUENO) ?? 0) + 1);
    return [...cuenta.entries()].sort((a, b) => (a[0] === SIN_DUENO ? 1 : b[0] === SIN_DUENO ? -1 : b[1] - a[1])).map(([oid]) => oid);
  }, [casos]);
  const etiquetas = useMemo(() => [...new Set(casos.flatMap((c) => c.etiquetas ?? []))].sort(), [casos]);
  const nombreDe = (oid: string) => (oid === SIN_DUENO ? 'Sin asignar' : (nombres.get(oid) ?? oid));
  const alternar = <K extends 'duenos' | 'tipos' | 'prioridades' | 'etiquetas'>(k: K, v: FiltrosRapidos[K][number]) => {
    const actual = filtros[k] as string[];
    alCambiar({ ...filtros, [k]: actual.includes(v) ? actual.filter((x) => x !== v) : [...actual, v] });
  };

  const chips: { clave: string; texto: string; quitar: () => void }[] = [
    ...filtros.duenos.map((d) => ({ clave: `d-${d}`, texto: `Responsable: ${nombreDe(d)}`, quitar: () => alternar('duenos', d) })),
    ...filtros.tipos.map((t) => ({ clave: `t-${t}`, texto: `Tipo: ${ETIQUETA_TIPO[t]}`, quitar: () => alternar('tipos', t) })),
    ...filtros.prioridades.map((p) => ({ clave: `p-${p}`, texto: `Prioridad: ${ETIQUETA_PRIORIDAD[p]}`, quitar: () => alternar('prioridades', p) })),
    ...filtros.etiquetas.map((e) => ({ clave: `e-${e}`, texto: `Etiqueta: ${e}`, quitar: () => alternar('etiquetas', e) })),
    ...(filtros.mios ? [{ clave: 'mios', texto: 'Solo mis casos', quitar: () => alCambiar({ ...filtros, mios: false }) }] : []),
    ...(filtros.vencidos ? [{ clave: 'venc', texto: 'Vencidos', quitar: () => alCambiar({ ...filtros, vencidos: false }) }] : []),
  ];

  return (
    <div className="barra-filtros" role="search" aria-label="Filtros">
      <label className="buscar-local">
        <Icono nombre="buscar" />
        <span className="sr">Buscar</span>
        <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar cliente, caso o tema" />
      </label>

      <div className="filtro-personas" role="group" aria-label="Filtrar por responsable">
        {duenos.slice(0, 5).map((oid) => {
          const on = filtros.duenos.includes(oid);
          return (
            <button key={oid} type="button" className={`filtro-persona ${on ? 'filtro-persona--on' : ''}`} aria-pressed={on} onClick={() => alternar('duenos', oid)} data-ayuda={nombreDe(oid)} data-ayuda-pos="abajo" aria-label={`Responsable: ${nombreDe(oid)}`}>
              <Avatar oid={oid === SIN_DUENO ? null : oid} nombre={oid === SIN_DUENO ? null : nombreDe(oid)} tam={32} titulo={false} />
            </button>
          );
        })}
      </div>

      <Popover
        ancho={280}
        rol="menu"
        etiqueta="Agregar filtro"
        disparador={({ ref, alternar: abrir, props }) => (
          <button ref={ref} type="button" className="chip-filtro chip-filtro--agregar" onClick={abrir} {...props}>
            <Icono nombre="mas" />
            Agregar filtro
          </button>
        )}
      >
        <div className="menu menu--filtros" role="menu" aria-label="Agregar filtro">
          <SeparadorMenu titulo="Rápidos" />
          <ItemMenu rol="menuitemcheckbox" seleccionado={filtros.mios} onClick={() => alCambiar({ ...filtros, mios: !filtros.mios })}>
            Solo mis casos
          </ItemMenu>
          <ItemMenu rol="menuitemcheckbox" seleccionado={filtros.vencidos} onClick={() => alCambiar({ ...filtros, vencidos: !filtros.vencidos })}>
            Vencidos
          </ItemMenu>
          <SeparadorMenu titulo="Responsable" />
          {duenos.map((oid) => (
            <ItemMenu key={oid} rol="menuitemcheckbox" seleccionado={filtros.duenos.includes(oid)} icono={<Avatar oid={oid === SIN_DUENO ? null : oid} nombre={oid === SIN_DUENO ? null : nombreDe(oid)} tam={20} titulo={false} />} onClick={() => alternar('duenos', oid)}>
              {nombreDe(oid)}
            </ItemMenu>
          ))}
          <SeparadorMenu titulo="Tipo" />
          {TIPOS.map((t) => (
            <ItemMenu key={t} rol="menuitemcheckbox" seleccionado={filtros.tipos.includes(t)} icono={<IconoTipo tipo={t} />} onClick={() => alternar('tipos', t)}>
              {ETIQUETA_TIPO[t]}
            </ItemMenu>
          ))}
          <SeparadorMenu titulo="Prioridad" />
          {PRIORIDADES.map((p: Prioridad) => (
            <ItemMenu key={p} rol="menuitemcheckbox" seleccionado={filtros.prioridades.includes(p)} icono={<IconoPrioridad prioridad={p} />} onClick={() => alternar('prioridades', p)}>
              {ETIQUETA_PRIORIDAD[p]}
            </ItemMenu>
          ))}
          {etiquetas.length > 0 && <SeparadorMenu titulo="Etiquetas" />}
          {etiquetas.map((e) => (
            <ItemMenu key={e} rol="menuitemcheckbox" seleccionado={filtros.etiquetas.includes(e)} icono={<Icono nombre="etiqueta" />} onClick={() => alternar('etiquetas', e)}>
              {e}
            </ItemMenu>
          ))}
        </div>
      </Popover>

      {chips.map((c) => (
        <span key={c.clave} className="chip-filtro chip-filtro--activo">
          {c.texto}
          <button type="button" onClick={c.quitar} aria-label={`Quitar filtro ${c.texto}`}>
            <Icono nombre="cerrar" />
          </button>
        </span>
      ))}
      {hayFiltros(filtros) && (
        <button
          type="button"
          className="btn btn--enlace"
          onClick={() => {
            setTexto('');
            alCambiar(FILTROS_VACIOS);
          }}
        >
          Limpiar
        </button>
      )}
      {extra && <div className="barra-filtros__extra">{extra}</div>}
    </div>
  );
}
